import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Statistics — the strand where the right answer stops being the whole answer.
 *
 * These lessons are written around one idea the design document cares about:
 * a summary is a *choice*, and every choice hides something. Students who learn
 * "the average is the middle one" without learning what it conceals are being
 * taught a procedure, not statistics. So the displays lesson names what each
 * chart leaves out, and spread is taught as the recovery of exactly what the
 * average discarded.
 *
 * `sampling-01` is Grade 7 rather than Grade 6, and it is where the strand
 * turns: every earlier lesson summarises data you have, and this one asks what
 * may honestly be said about data you do not.
 */
export const STATISTICS_LESSONS: Lesson[] = [
  lesson({
    id: 'data-displays-01',
    unitId: 'unit-statistics',
    conceptId: 'data-displays',
    title: 'Data and Graphs',
    summary: 'The pictures that summarise data — how to read them, and what each one leaves out.',
    objectives: [
      'Read values from a bar chart and a dot plot',
      'Choose a display that suits the question',
      'Describe the shape and spread of a data set',
    ],
    estimatedMinutes: 25,
    sections: {
      introduction: {
        title: 'Twenty numbers, one glance',
        blocks: [
          text('Here are the number of books twenty children read last month:'),
          text('3, 5, 2, 4, 3, 6, 3, 4, 5, 2, 4, 3, 7, 4, 3, 5, 4, 2, 3, 4'),
          text('Nothing about that list is easy to see. Draw it, and the pattern is immediate.'),
        ],
      },
      intuition: {
        title: 'A graph answers one question well',
        blocks: [
          text('There is no best chart, only a chart suited to the question you are asking.'),
          table(['Display', 'Good for', 'Hides'], [
            ['Bar chart', 'Comparing separate categories', 'Anything within a category'],
            ['Dot plot', 'Seeing every value and where they clump', 'Gets crowded with large data sets'],
            ['Line graph', 'Change over time', 'What happened between the points'],
          ]),
          warning('Every summary throws something away. That is what makes it a summary — but it is worth knowing what you have thrown away before you draw a conclusion from what is left.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'dot-plot', values: [3, 5, 2, 4, 3, 6, 3, 4, 5, 2, 4, 3, 7, 4, 3, 5, 4, 2, 3, 4], from: 1, to: 8, label: 'books read last month' },
            'One dot per child. The values clump around 3 and 4, with a lone reader out at 7.',
          ),
          text('Now the same idea for categories rather than counts:'),
          figure(
            { kind: 'bar-chart', bars: [{ label: 'Mon', value: 12 }, { label: 'Tue', value: 18 }, { label: 'Wed', value: 9 }, { label: 'Thu', value: 15 }, { label: 'Fri', value: 22 }], unitLabel: 'books borrowed from the library' },
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Using the dot plot above: how many children read exactly 4 books, and how many read more than 4?'),
          steps([
            { label: 'Count the dots stacked above 4.', latex: '6 \\text{ children}' },
            { label: 'For "more than 4", count every dot to the right of 4 — not including 4 itself.', latex: '5, 5, 5, 6, 7' },
            { label: 'Answer.', latex: '5 \\text{ children}' },
          ]),
          tip('"More than 4" and "4 or more" are different questions with different answers. Read the wording before you count.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('data-displays', 4, 'Read values from the display.')] },
      independent: { title: 'On your own', blocks: [practice('data-displays', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('data-displays', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A dot plot shows every value and where they clump.',
            'A bar chart compares separate categories.',
            'Read the axis labels and the scale before reading any value.',
            'Every display hides something; know what.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'mean-median-mode-01',
    unitId: 'unit-statistics',
    conceptId: 'mean-median-mode',
    title: 'Averages',
    summary: 'Three ways to name a typical value, and why they disagree.',
    objectives: [
      'Calculate the mean, median and mode',
      'Choose the most honest average for a data set',
      'Explain how an outlier affects each one',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: '"Average" is not one thing',
        blocks: [
          text('Seven people work at a small company. Six earn £25,000 and the owner earns £200,000.'),
          text('The average salary is £50,000. Every single employee earns less than the average. The number is arithmetically correct and completely misleading — which is why one average is never enough.'),
        ],
      },
      intuition: {
        title: 'Three different questions',
        blocks: [
          list([
            '**Mean** — share everything out equally. Add all the values and divide by how many there are.',
            '**Median** — put the values in order and take the middle one.',
            '**Mode** — the value that appears most often.',
          ]),
          insight('In that company: the mean is £50,000, the median is £25,000, and the mode is £25,000. Two of those describe the workforce honestly. The mean describes a workforce that does not exist.'),
          warning('The mean is dragged by extreme values; the median barely notices them. When one value is far from the rest, the median is usually the more honest summary.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'dot-plot', values: [2, 3, 3, 3, 4, 4, 5, 12], from: 1, to: 13, label: 'one value sits far from the rest' },
            'The dot at 12 pulls the mean to the right. The median stays with the cluster.',
          ),
          table(['Average', 'Value', 'Effect of the outlier'], [
            ['Mean', '4.5', 'Pulled well above the cluster'],
            ['Median', '3.5', 'Almost unaffected'],
            ['Mode', '3', 'Not affected at all'],
          ]),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the mean, median and mode of: 7, 3, 9, 3, 8.'),
          steps([
            { label: 'Mean: add every value.', latex: '7 + 3 + 9 + 3 + 8 = 30' },
            { label: 'Divide by how many values there are — five, not four.', latex: '30 \\div 5 = 6' },
            { label: 'Median: sort the values first. This step is not optional.', latex: '3, \; 3, \; 7, \; 8, \; 9' },
            { label: 'Take the middle of the sorted list.', latex: '7' },
            { label: 'Mode: the value appearing most often.', latex: '3' },
          ]),
          warning('Taking the middle of the unsorted list would have given 9 — a number that is not typical of anything here. Always sort before finding a median.'),
          tip('With an even number of values there is no single middle, so the median is the mean of the two middle ones.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('mean-median-mode', 4)] },
      independent: { title: 'On your own', blocks: [practice('mean-median-mode', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('mean-median-mode', 3, 'Data sets with outliers, and working backwards from a mean.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Mean: add everything, divide by how many.',
            'Median: sort first, then take the middle.',
            'Mode: the most frequent value.',
            'An outlier moves the mean a long way and the median hardly at all.',
          ]),
          insight('Whenever someone quotes "the average", the useful question is: which one, and what does it hide?'),
        ],
      },
    },
  }),

  lesson({
    id: 'histograms-01',
    unitId: 'unit-statistics',
    conceptId: 'histograms',
    title: 'Histograms and Statistical Questions',
    summary: 'Group measurements into intervals and a data set stops being a list of numbers and starts having a shape.',
    objectives: [
      'Tell a statistical question from one with a single answer',
      'Build and read a histogram',
      'Say why a histogram is not a bar chart',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A question worth collecting data for',
        blocks: [
          text('"How tall am I?" has one answer, and no amount of data collection improves it.'),
          text('"How tall are the students in this school?" has no single answer, and that is exactly what makes it a **statistical question**. It expects variability, and the answer is the whole spread of values rather than any one of them.'),
          insight('Statistics begins the moment you accept that the answer is a distribution, not a number. Everything after that — displays, averages, spread — is a way of describing one.'),
        ],
      },
      intuition: {
        title: 'When every value is different, stop counting values',
        blocks: [
          text('A dot plot works when values repeat. Ask forty people how far they walked and you may get forty different numbers, each with a single dot — a row of ones that shows nothing.'),
          text('So group them. Choose equal intervals — 0 up to 10, 10 up to 20, and so on — and count how many measurements fall in each. Those counts are the **frequencies**, and the picture of them is a **histogram**.'),
          warning('The bars of a histogram touch. A bar chart shows separate categories that could be rearranged in any order; a histogram shows one continuous measurement that has been chopped into intervals, and those intervals have a fixed order with nothing between them.', 'Not the same as a bar chart'),
          tip('Every interval must be the same width, or a tall bar might mean nothing more than a wide bin.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Forty people, asked how many minutes they spent travelling to work:'),
          figure(
            {
              kind: 'histogram',
              bins: [
                { label: '0–9', count: 5 },
                { label: '10–19', count: 12 },
                { label: '20–29', count: 14 },
                { label: '30–39', count: 6 },
                { label: '40–49', count: 3 },
              ],
              unitLabel: 'minutes travelling to work',
            },
            'The shape is the answer: most journeys are between 10 and 30 minutes, with a thin tail of long ones.',
          ),
          text('A histogram trades detail for shape. You can no longer see that someone travelled exactly 23 minutes — and you can now see, at a glance, something no list of forty numbers would have shown you.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Using the histogram above: how many people travelled less than 20 minutes, and what fraction of everyone is that?'),
          steps([
            { label: 'Less than 20 covers the first two bars.', latex: '5 + 12' },
            { label: 'Add them.', latex: '17 \\text{ people}' },
            { label: 'Total everyone by adding all five bars.', latex: '5 + 12 + 14 + 6 + 3 = 40' },
            { label: 'Write the comparison as a fraction.', latex: '\\frac{17}{40}' },
          ]),
          warning('The height of a bar is a *count of people*, not a value of the measurement. The tallest bar here is 14, and 14 is not a number of minutes — it is fourteen travellers.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('histograms', 4, 'Read the histogram.')] },
      independent: { title: 'On your own', blocks: [practice('histograms', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('histograms', 3)] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A statistical question expects variability in its answers.',
            'A histogram groups a continuous measurement into equal intervals.',
            'Bar heights are frequencies — counts, not measurements.',
            'The bars touch, because the intervals are next to each other on one scale.',
            'Grouping hides individual values and reveals the shape.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'data-variability-01',
    unitId: 'unit-statistics',
    conceptId: 'data-variability',
    title: 'Spread and Variability',
    summary: 'Two data sets can share an average and share nothing else. Spread measures the part the average threw away.',
    objectives: [
      'Compute the range and the interquartile range',
      'Compute the mean absolute deviation',
      'Choose a measure of spread that suits the data',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Same average, different worlds',
        blocks: [
          text('Two classes sit the same test out of 20. Both have a mean of 12.'),
          text('Class A: 11, 12, 12, 12, 13. Class B: 2, 6, 12, 18, 22.'),
          text('The averages are identical and the classes are nothing alike. Every summary hides something; the mean hides *spread*, and this lesson is about measuring what it hid.'),
        ],
      },
      intuition: {
        title: 'Three ways to say how spread out',
        blocks: [
          text('**Range** — largest minus smallest. Quick, and completely at the mercy of one unusual value.'),
          text('**Interquartile range** — the width of the middle half. Sort the data, find the median, then find the median of the lower half (Q1) and of the upper half (Q3). The IQR is Q3 − Q1, and one extreme value cannot move it.'),
          text('**Mean absolute deviation** — the average distance from the mean. It uses every value, which is what makes it the most honest of the three and the most work.'),
          warning('Deviations from the mean always add to zero — that is what being the mean means. Taking the *absolute* value of each distance first is what stops the answer collapsing to nothing.', 'Why the bars are there'),
          insight('Range is one subtraction, IQR ignores the outliers, MAD listens to everybody. Which you want depends on whether the unusual values are noise or news.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('The five-number summary of Class B — minimum, Q1, median, Q3, maximum — drawn as a box plot:'),
          figure(
            { kind: 'box-plot', min: 2, q1: 6, median: 12, q3: 18, max: 22, from: 0, to: 24, label: 'Class B test scores' },
            'The box holds the middle half of the data. Its width is the interquartile range: 18 − 6 = 12.',
          ),
          text('And the same picture for Class A, on the same scale:'),
          figure(
            { kind: 'box-plot', min: 11, q1: 11.5, median: 12, q3: 12.5, max: 13, from: 0, to: 24, label: 'Class A test scores' },
            'Same median, and almost no box at all. This is the difference the mean could not show you.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the range, the interquartile range and the mean absolute deviation of 4, 8, 9, 11, 13.'),
          steps([
            { label: 'The data is already sorted. Range is largest minus smallest.', latex: '13 - 4 = 9' },
            { label: 'The median is the middle value.', latex: '9' },
            { label: 'Q1 is the median of the values below it; Q3 the median of those above.', latex: 'Q_1 = 6, \\quad Q_3 = 12' },
            { label: 'The interquartile range is the width of the box.', latex: 'Q_3 - Q_1 = 12 - 6 = 6' },
          ]),
          text('Now the mean absolute deviation, which needs the mean first:'),
          steps([
            { label: 'Add and divide.', latex: '\\frac{4 + 8 + 9 + 11 + 13}{5} = \\frac{45}{5} = 9' },
            { label: 'Take each distance from 9, ignoring direction.', latex: '5, \\; 1, \\; 0, \\; 2, \\; 4' },
            { label: 'Average those distances.', latex: '\\frac{5 + 1 + 0 + 2 + 4}{5} = \\frac{12}{5} = 2.4' },
            { label: 'So a typical score sits about 2.4 from the mean.', latex: '\\text{MAD} = 2.4' },
          ]),
          tip('Sense-check a MAD against the range: it should be comfortably smaller. A mean absolute deviation larger than the range is arithmetic gone wrong, not unusual data.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('data-variability', 4)] },
      independent: { title: 'On your own', blocks: [practice('data-variability', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('data-variability', 3, 'Compare two data sets that share an average.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Two data sets with the same mean can be completely different.',
            'Range is largest minus smallest, and one outlier controls it.',
            'The interquartile range Q3 − Q1 is the width of the middle half.',
            'Mean absolute deviation is the average distance from the mean, using absolute distances.',
            'A box plot shows all five summary numbers at once.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'sampling-01',
    unitId: 'unit-samples',
    conceptId: 'sampling',
    title: 'Samples and Inferences',
    summary: 'Saying something about a population you cannot count, from a handful you can — and being honest about what that leaves uncertain.',
    objectives: [
      'Tell a population from a sample',
      'Scale a proportion found in a sample up to the whole population',
      'Recognise a sample chosen in a way that makes its answer worthless',
      'Judge how much a conclusion should be trusted',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Asking some to learn about all',
        blocks: [
          text('A school has 800 students and wants to know how many walk to school. Asking all 800 would settle it — and nobody does that. They ask 40, find that 14 walk, and say: about 280 of the 800.'),
          math('\\frac{14}{40} = 0.35 \\qquad 0.35 \\times 800 = 280'),
          text('The arithmetic is proportional reasoning you already have. What is new is the claim being made: a statement about 800 people, 760 of whom were never asked.'),
          insight('The 800 are the **population**. The 40 are the **sample**. Everything in this lesson is about when the second is allowed to speak for the first.'),
        ],
      },
      intuition: {
        title: 'When a sample may speak for a population',
        blocks: [
          text('A sample is trustworthy when every member of the population had a fair chance of being in it. That is what **random** means here — not "chosen carelessly", but chosen by something that has no opinion, like a computer picking 40 names from the roll.'),
          text('Compare three ways of picking 40 students:'),
          table(['How the 40 were chosen', 'Trustworthy?', 'Why'], [
            ['40 names drawn at random from the roll', 'yes', 'everyone could have been picked'],
            ['the first 40 through the gate', 'no', 'walkers arrive early; drivers arrive late'],
            ['40 students in the cycling club', 'no', 'almost nobody in it walks'],
          ]),
          warning('A biased sample is not fixed by making it bigger. Asking 400 members of the cycling club instead of 40 gives a more precise answer to the wrong question — you learn about cyclists, very accurately.', 'Bias does not average out'),
          text('Size does matter, but for a different reason. A larger random sample gives an answer that moves around less. Ask 10 students on three separate days and you might get 2, 5 and 3 walkers; ask 100 and the proportion barely shifts.'),
          insight('Random fixes *bias* — being wrong in a consistent direction. Size fixes *variability* — being wrong by a different amount each time. They are different problems and a sample needs both.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          text('Four random samples of 40, taken from the same school on the same day:'),
          figure(
            {
              kind: 'bar-chart',
              bars: [
                { label: 'Sample A', value: 14 },
                { label: 'Sample B', value: 12 },
                { label: 'Sample C', value: 16 },
                { label: 'Sample D', value: 13 },
              ],
              unitLabel: 'walkers out of 40',
            },
            'Every sample gives a different number, and none of them is the mistake. Variation between samples is what sampling is.',
          ),
          text('Scaled up, those four samples estimate 280, 240, 320 and 260 walkers. The honest report is not a single number but a range: somewhere around 250 to 300.'),
          tip('Say "about", and say how many you asked. "About 280 of the 800, based on a random sample of 40" is a claim someone can judge. "280 students walk to school" is a claim that is almost certainly false to the exact student.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A lake is stocked with fish. 60 are caught at random, tagged and released. A week later 80 fish are caught and 6 of them carry tags. Estimate how many fish are in the lake.'),
          steps([
            { label: 'The second catch is the sample. Find the proportion tagged in it.', latex: '\\frac{6}{80} = 0.075' },
            { label: 'Assume the tagged fish mixed evenly, so the same proportion of the whole lake is tagged.', latex: '\\frac{60}{N} = 0.075' },
            { label: 'Solve for the population.', latex: 'N = \\frac{60}{0.075} = 800' },
            { label: 'Report it as an estimate, not a count.', latex: '\\approx 800\\text{ fish}' },
          ]),
          text('Every part of this can be questioned, and that is the point. If tagged fish avoid nets afterwards, 6 is too low and the estimate is too high. A statistician does not stop at the number; they say what the number assumed.'),
          tip('Check the direction before you divide. Fewer tags in the second catch means the tagged fish are diluted among *more* fish — so a small proportion gives a large population, not a small one.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('sampling', 4)] },
      independent: { title: 'On your own', blocks: [practice('sampling', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('sampling', 3, 'Judging which samples may be trusted, and working back from a sample to a population size.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A sample stands for a population only if it was chosen randomly.',
            'Scale the proportion, not the count: 14 out of 40 becomes 35% of 800, not 14 of 800.',
            'A bigger sample does not repair a biased one.',
            'Different samples give different answers. Report a range, and say how many you asked.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'standard-deviation-01',
    unitId: 'unit-data-spread',
    conceptId: 'standard-deviation',
    title: 'Standard Deviation',
    summary: 'The measure of spread the rest of statistics is built on — and why it squares the distances before averaging them.',
    objectives: [
      'Compute a standard deviation from a small data set',
      'Say what a standard deviation means in the units of the data',
      'Compare two data sets by spread rather than by centre',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'The third attempt at spread',
        blocks: [
          text('Grade 6 measured spread three ways: the range, the interquartile range, and the mean absolute deviation. The MAD was the honest one — it used every value, taking each distance from the mean positively.'),
          text('**Standard deviation** does almost exactly that, with one change: it squares each distance instead of taking it positively, averages the squares, and then takes a square root to undo the squaring at the end.'),
          math('s = \\sqrt{\\frac{\\sum (x - \\bar{x})^{2}}{n}}'),
          text('That looks like a detour to arrive back where MAD already was. It is not, and the reason is worth having.'),
        ],
      },
      intuition: {
        title: 'Why square, and what the answer means',
        blocks: [
          text('Both squaring and taking the absolute value do the same immediate job: they stop the distances above the mean cancelling the ones below. Without one of them every data set has a spread of exactly zero, because the deviations from a mean always sum to nothing — that is what a mean *is*.'),
          insight('Squaring differs in weighting. A value 4 away contributes 16 while a value 2 away contributes 4, so being twice as far out counts four times as much. Standard deviation therefore notices an outlier that mean absolute deviation shrugs at, which is either the feature or the flaw depending on whether the outlier is news or noise.'),
          text('The final square root is not decoration. Squaring the distances squares the units — squared marks, squared centimetres — and rooting brings the answer back into the units of the original data, where it can be compared with the values themselves.'),
          warning('Do not stop before the root. The quantity under it has a name of its own — the **variance** — and it is a perfectly good number in the wrong units. A variance of 25 marks² is a standard deviation of 5 marks, and reporting 25 overstates the spread fivefold.', 'Variance is not the answer'),
          text('The result is a single number in the units of the data, answering "how far from the mean is a typical value?"'),
          table(['Data', 'Mean', 'Standard deviation', 'Reads as'], [
            ['11, 12, 12, 12, 13', '12', '0.63', 'almost everyone at the mean'],
            ['2, 6, 12, 18, 22', '12', '7.16', 'typical value 7 from the mean'],
            ['12, 12, 12, 12, 12', '12', '0', 'no spread at all — identical values'],
          ]),
          insight('The first two rows are the same two classes from the spread lesson: identical means, and nothing else in common. A standard deviation of 0 happens only when every value is the mean, and it is the one value the measure can never go below.'),
          tip('Sense-check against the range. A standard deviation is always smaller than the range and usually a good deal smaller — somewhere near a quarter of it for ordinary data. One larger than the range is arithmetic gone wrong.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'dot-plot', values: [11, 12, 12, 12, 13], from: 0, to: 24, label: 'Class A' },
            'Class A: every value within one of the mean. A small standard deviation is this picture — a tight cluster.',
          ),
          figure(
            { kind: 'dot-plot', values: [2, 6, 12, 18, 22], from: 0, to: 24, label: 'Class B' },
            'Class B: the same mean of 12, spread across the whole scale. The standard deviation is what tells the two apart, because the mean cannot.',
          ),
          text('Standard deviation is the measure the rest of statistics is built on — the normal distribution, margins of error and every claim about how surprising a result is are all stated in multiples of it. This is why it survives despite being more work than the alternatives.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Find the standard deviation of 4, 8, 9, 11, 13.'),
          steps([
            { label: 'Find the mean.', latex: '\\bar{x} = \\frac{4 + 8 + 9 + 11 + 13}{5} = 9' },
            { label: 'Take each deviation from it.', latex: '-5,\\ -1,\\ 0,\\ 2,\\ 4' },
            { label: 'Square them.', latex: '25,\\ 1,\\ 0,\\ 4,\\ 16' },
            { label: 'Average the squares — this is the variance.', latex: '\\frac{46}{5} = 9.2' },
            { label: 'Take the square root.', latex: 's = \\sqrt{9.2} \\approx 3.03' },
          ]),
          text('So a typical value sits about 3 from the mean of 9. The range is 9 and the standard deviation is 3.03 — comfortably smaller, as it must be. Note that the deviations in step two sum to zero, which is the check that the mean was right and the reason the squaring in step three is needed at all.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('standard-deviation', 4)] },
      independent: { title: 'On your own', blocks: [practice('standard-deviation', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('standard-deviation', 3, 'Comparing two sets by spread, and telling a variance from a standard deviation.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Standard deviation is the typical distance of a value from the mean.',
            'Squaring stops the deviations cancelling, and weights the far ones more heavily.',
            'The square root at the end returns the answer to the units of the data.',
            'Variance is the value before that root, in squared units — not the answer.',
            'It is zero only when every value is identical, and never negative.',
            'Two sets with the same mean can have completely different standard deviations.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'normal-distribution-01',
    unitId: 'unit-data-eleven',
    conceptId: 'normal-distribution',
    title: 'Normal Distributions',
    summary: 'The shape an enormous number of measurements turn out to have — and what a standard deviation is for once you have it.',
    objectives: [
      'Recognise a normal distribution and say what makes one',
      'Apply the 68–95–99.7 rule to find a proportion',
      'Compute a z-score and read it as a position',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The same shape, over and over',
        blocks: [
          text('Measure the heights of ten thousand adults and draw a histogram. Then do it with birth weights, or with the errors in ten thousand careful measurements of the same length, or with the total of a hundred dice.'),
          text('The bars come out in the same shape every time: a single hump at the middle, falling away symmetrically, thin at both ends and never quite reaching the axis.'),
          insight('That shape is the **normal distribution**, and its ubiquity is not a coincidence. Anything that is the sum of many small independent effects — genes and diet and sleep, in the case of height — ends up here, whatever the individual effects look like.'),
          text('Which means it is worth knowing one curve very well, because knowing it well tells you something about a great many measurements at once.'),
        ],
      },
      intuition: {
        title: 'Two numbers describe the whole thing',
        blocks: [
          text('A normal distribution is completely determined by two numbers: the **mean** μ, which says where the peak sits, and the **standard deviation** σ, which says how wide it is. Change μ and the curve slides; change σ and it gets fatter or thinner without moving.'),
          text('And because the shape is always the same, the proportion of data within a given number of standard deviations is always the same too:'),
          table(['Within', 'Proportion of the data', 'Left outside'], [
            ['1 standard deviation', '68%', '32% — 16% at each end'],
            ['2 standard deviations', '95%', '5% — 2.5% at each end'],
            ['3 standard deviations', '99.7%', '0.3% — 0.15% at each end'],
          ]),
          insight('This is the **68–95–99.7 rule**, and it is what makes a standard deviation useful rather than merely computable. Told that adult heights are normal with μ = 170 cm and σ = 8 cm, you now know that about two thirds of adults are between 162 and 178, and that someone under 146 is one person in about seven hundred.'),
          warning('Halve before you answer about one side. The curve is symmetric, so what is left over splits equally between the two tails — 16% above 178, not 32%. The rule states the *middle*; a question about one end needs the halving.', 'One tail, not two'),
          text('To compare a value against a distribution — or two values from different distributions — convert it into standard deviations. That is a **z-score**:'),
          math('z = \\frac{x - \\mu}{\\sigma}'),
          list([
            'z = 0 is exactly at the mean.',
            'z = 2 is two standard deviations above it, higher than about 97.5% of the data.',
            'z = −1.5 is one and a half below.',
          ]),
          warning('Both steps are needed. Subtracting the mean gives the distance in the original units; dividing by σ is what turns that distance into a count of standard deviations. Stopping halfway gives a number that cannot be compared with anything.', 'Subtract, then divide'),
          insight('A z-score is what lets you compare a mark of 78 in one exam with 65 in another. Whichever is further above its own mean, measured in its own standard deviations, is the better performance — and the raw numbers cannot say.'),
          warning('None of this applies to data that is not bell-shaped. Incomes have a long right tail, waiting times pile up near zero, and a test everyone finds easy bunches against full marks. The percentages above are properties of the normal curve, not of data in general.', 'Only for normal data'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'normal-curve',
              mean: 170,
              sd: 8,
              shade: { from: -1, to: 1, label: '68%' },
              unitLabel: 'adult height (cm)',
              label: 'μ = 170, σ = 8',
            },
            'Heights with mean 170 cm and standard deviation 8 cm. The shaded middle holds 68% of people, so 16% are above 178 and 16% below 162.',
          ),
          figure(
            {
              kind: 'normal-curve',
              mean: 170,
              sd: 8,
              shade: { from: -2, to: 2, label: '95%' },
              unitLabel: 'adult height (cm)',
              label: 'two standard deviations',
            },
            'The same curve, out to two standard deviations. Widening the band from 68% to 95% costs only 8 cm at each end — which is why being three standard deviations out is so rare.',
          ),
          tip('Sketch the curve and shade the region before computing anything. Almost every wrong answer in this topic is the right arithmetic applied to the wrong side of the value, and a shaded sketch makes that impossible to do quietly.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A machine fills bottles with a mean of 500 ml and a standard deviation of 4 ml, normally distributed. What proportion of bottles hold more than 508 ml? And what is the z-score of a 494 ml bottle?'),
          steps([
            { label: 'How far is 508 from the mean, in standard deviations?', latex: '\\frac{508 - 500}{4} = 2' },
            { label: 'Within two standard deviations lies 95%.', latex: '100\\% - 95\\% = 5\\%' },
            { label: 'That 5% is split between two tails, and we want one.', latex: '\\frac{5\\%}{2} = 2.5\\%' },
            { label: 'Now the second bottle.', latex: 'z = \\frac{494 - 500}{4} = -1.5' },
          ]),
          text('So about 2.5% of bottles are over 508 ml, and the 494 ml bottle sits one and a half standard deviations below the mean. The negative sign is information, not an error — it says which side of the mean the bottle is on, and dropping it would lose exactly that.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('normal-distribution', 4)] },
      independent: { title: 'On your own', blocks: [practice('normal-distribution', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('normal-distribution', 3, 'One-tail proportions, and z-scores compared across two distributions.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A normal distribution is symmetric, single-peaked, and fixed by its mean and standard deviation.',
            'About 68%, 95% and 99.7% lie within one, two and three standard deviations of the mean.',
            'Those figures describe the middle — halve what is left over for a single tail.',
            'z = (x − μ) ÷ σ counts how many standard deviations a value is from the mean.',
            'A negative z-score means below the mean, and the sign is part of the answer.',
            'Skewed or bounded data is not normal, and none of these percentages apply to it.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'regression-01',
    unitId: 'unit-data-eleven',
    conceptId: 'regression',
    title: 'Regression and Correlation',
    summary: 'Putting a line through a cloud of points, measuring how well it fits, and knowing where it stops meaning anything.',
    objectives: [
      'Use a line of best fit to predict, and to interpret its slope',
      'Interpret a correlation coefficient, including its sign',
      'Say why a prediction outside the data is not supported by the line',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Which line?',
        blocks: [
          text('Grade 8 drew a line through a scatter plot by eye, and that was honest as far as it went: two people drawing by eye get two different lines, and nothing in the method decides between them.'),
          text('**Regression** is what decides. For each point, the line either overshoots or undershoots by some amount — the **residual**, measured vertically:'),
          math('\\text{residual} = \\text{actual} - \\text{predicted}'),
          insight('The **least-squares line** is the one that makes the total of the squared residuals as small as possible. Squaring is here for the same reason it was in standard deviation: it stops overshoots cancelling undershoots, and it weights a badly-missed point more heavily than a nearly-hit one.'),
          text('There is exactly one such line for any set of points, which is what turns "draw a line of best fit" from a matter of taste into a calculation.'),
        ],
      },
      intuition: {
        title: 'The line, and how much to trust it',
        blocks: [
          text('The line is written as any line is, and both of its numbers mean something about the data:'),
          math('\\hat{y} = a + bx'),
          list([
            '**b**, the slope, is the predicted change in y for a one-unit increase in x. It carries the units of the data — "3.2 cm per year", not "3.2".',
            '**a**, the intercept, is the prediction at x = 0 — which is often outside the data and therefore often meaningless.',
            'The hat on ŷ is a reminder: this is a prediction, not a measurement.',
          ]),
          text('The line exists whatever the points look like, so a second number is needed to say whether it deserves attention. That is the **correlation coefficient** r:'),
          table(['r', 'What it means'], [
            ['+1', 'perfect: every point exactly on an upward line'],
            ['+0.8', 'strong upward association'],
            ['+0.3', 'weak upward association'],
            ['0', 'no linear association at all'],
            ['−0.9', 'strong downward association'],
            ['−1', 'perfect: every point exactly on a downward line'],
          ]),
          warning('The sign of r is the direction and its size is the strength. r = −0.9 is a strong relationship, exactly as strong as +0.9 — ranking coefficients by value rather than by size calls the strongest relationship in a table the weakest.', 'Sign is direction, size is strength'),
          warning('Slope and correlation are different questions and neither implies the other. A gentle line through points sitting in a perfect row has r = 1; a steep line through a shapeless cloud has r near zero. Steepness is not fit.', 'A steeper line is not a better one'),
          text('Two limits are worth as much as the technique itself. The first is old: **association is not causation**, and a line through a scatter plot cannot tell you which way the arrow points or whether there is an arrow at all.'),
          warning('The second is **extrapolation**. The line was fitted to the range of x you actually observed, and outside that range nothing was measured. A line fitting the heights of children aged 5 to 15 will predict a nine-foot adult at 40, and it will do so with a perfectly straight face.', 'The line stops where the data does'),
          insight('An r near ±1 says the *line* fits, and nothing more. It does not say the relationship is causal, that it continues outside the data, or that a line is the right shape — a perfect parabola through the origin can have an r of zero.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 3 },
                { x: 2, y: 4 },
                { x: 3, y: 4 },
                { x: 4, y: 6 },
                { x: 5, y: 6 },
                { x: 6, y: 8 },
                { x: 7, y: 8 },
                { x: 8, y: 9 },
              ],
              lines: [{ slope: 0.9, intercept: 2.1, label: 'ŷ = 2.1 + 0.9x' }],
            },
            'Points hugging the line closely: a strong positive correlation, r ≈ 0.98. The vertical gaps between each point and the line are the residuals the fit made as small as it could.',
          ),
          figure(
            {
              kind: 'coordinate-grid',
              min: 0,
              max: 12,
              points: [
                { x: 1, y: 7 },
                { x: 2, y: 3 },
                { x: 3, y: 9 },
                { x: 4, y: 4 },
                { x: 5, y: 8 },
                { x: 6, y: 3 },
                { x: 7, y: 9 },
                { x: 8, y: 5 },
              ],
              lines: [{ slope: -0.1, intercept: 6.4, label: 'the same method' }],
            },
            'The same calculation on a shapeless cloud. A least-squares line always exists — that it exists is no evidence that it means anything, which is what r is for.',
          ),
          tip('Look at the scatter before quoting r, and quote r before quoting the line. Each answers a question the next one assumes.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Study time (hours) against test score gives ŷ = 41 + 6.5x for students studying between 1 and 6 hours, with r = 0.86. Interpret the slope, predict the score for 4 hours, and comment on predicting the score for 20 hours.'),
          steps([
            { label: 'The slope is the change in y per unit of x.', latex: 'b = 6.5', note: 'Each extra hour of study is associated with about 6.5 more marks.' },
            { label: 'Predict at x = 4.', latex: '\\hat{y} = 41 + 6.5(4) = 67' },
            { label: 'Check r before trusting it.', latex: 'r = 0.86', note: 'Strong and positive, so the line is worth using.' },
            { label: 'Now try x = 20.', latex: '\\hat{y} = 41 + 6.5(20) = 171' },
          ]),
          text('A score of 171 out of 100 is the answer arithmetic gives and the data does not support. Nobody in the study worked 20 hours, so the line has no evidence about what happens there — and the impossible number is a mercy, because an extrapolation that lands on a plausible-looking figure is just as unfounded and much harder to spot.'),
          insight('Note also what the strong r does not license. It says score and study time move together tightly; it does not establish that studying causes the marks, since students who study more may also be the ones who attend more and sleep better.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('regression', 4)] },
      independent: { title: 'On your own', blocks: [practice('regression', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('regression', 3, 'Residuals, the sign of r, and predictions the data cannot support.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The least-squares line minimises the total of the squared residuals, and there is exactly one of them.',
            'A residual is actual − predicted, so a point above the line has a positive one.',
            'The slope is the predicted change in y per unit of x, in the units of the data.',
            'r measures how tightly the points hug the line: sign for direction, size for strength.',
            'A steep line and a strong correlation are different things.',
            'Outside the range of the data the line predicts nothing, however good the fit inside it.',
            'Association is still not causation, however close r is to 1.',
          ]),
        ],
      },
    },
  }),
];
